export const DEFAULT_STAGE_COLORS = {
  procurement: '#fb923c',
  preprocessing: '#84cc16',
  processing: '#22c55e',
  storage: '#06b6d4',
  sales: '#8b5cf6',
  inventory: '#f59e0b',
  finance: '#f43f5e',
  hr: '#ec4899',
  analytics: '#6366f1',
  reports: '#64748b',
  masters: '#71717a',
  admin: '#60a5fa',
};

export const STAGE_LABELS = {
  procurement: 'Procurement',
  preprocessing: 'Pre-Processing',
  processing: 'Processing / Production',
  storage: 'Cold Store',
  sales: 'Sales & Dispatch',
  inventory: 'Inventory',
  finance: 'Accounts & HRM',
  hr: 'HR & Payroll',
  analytics: 'Analytics',
  reports: 'Reports',
  masters: 'Masters',
  admin: 'Admin',
};

const HEX_RE = /^#([0-9a-f]{6})$/i;

export const normalizeHex = (value, fallback = '#6366f1') => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
    return `#${trimmed.slice(1).split('').map((c) => c + c).join('')}`.toLowerCase();
  }
  return HEX_RE.test(trimmed) ? trimmed.toLowerCase() : fallback;
};

const hexToRgb = (hex) => {
  const clean = normalizeHex(hex).slice(1);
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
};

const mix = (hex, target, weight) => {
  const a = hexToRgb(hex);
  const b = hexToRgb(target);
  const rgb = {
    r: Math.round(a.r * (1 - weight) + b.r * weight),
    g: Math.round(a.g * (1 - weight) + b.g * weight),
    b: Math.round(a.b * (1 - weight) + b.b * weight),
  };
  return `#${[rgb.r, rgb.g, rgb.b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

const luminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  const channel = (v) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

export const getStageTheme = (stageKey, overrides = {}) => {
  const base = normalizeHex(overrides?.[stageKey], DEFAULT_STAGE_COLORS[stageKey] || DEFAULT_STAGE_COLORS.analytics);
  const darkText = mix(base, '#0f172a', 0.48);
  return {
    color: base,
    bg: mix(base, '#ffffff', 0.88),
    bgStrong: mix(base, '#ffffff', 0.78),
    border: mix(base, '#ffffff', 0.55),
    text: darkText,
    contrast: luminance(base) > 0.56 ? '#0f172a' : '#ffffff',
  };
};

export const getStageThemes = (overrides = {}) => (
  Object.fromEntries(Object.keys(DEFAULT_STAGE_COLORS).map((key) => [key, getStageTheme(key, overrides)]))
);

export const getStageKeyForPath = (pathname = '/') => {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/purchase-booking' || path === '/purchase-invoices' || path === '/purchase-risk-alerts' || path === '/parties' || path === '/party-ledger' || path === '/vehicle-compliance') return 'procurement';
  if (path === '/procurement' || path === '/procurement-batches' || path === '/raw-material-pool' || path.startsWith('/preprocessing')) return 'preprocessing';
  if (path.startsWith('/production') || path === '/workflow' || path.startsWith('/qc')) return 'processing';
  if (path.startsWith('/cold-storage') || path === '/finished-goods' || path.startsWith('/clients/intelligence')) return 'storage';
  if (path.startsWith('/sales')) return 'sales';
  if (path.startsWith('/inventory')) return 'inventory';
  if (path.startsWith('/accounts') || path.startsWith('/hr')) return 'finance';
  if (path.startsWith('/analytics') || path === '/traceability') return 'analytics';
  if (path.startsWith('/reports')) return 'reports';
  if (path.startsWith('/masters')) return 'masters';
  if (path.startsWith('/admin')) return 'admin';
  return 'analytics';
};

export const getStageThemeForPath = (pathname = '/') => getStageTheme(getStageKeyForPath(pathname));
